import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { stripeConnectOnboard, stripeConnectStatus } from '../lib/api';
import { CheckCircle2, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const StripeConnectPanel = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [connectError, setConnectError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await stripeConnectStatus();
      setStatus(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleConnect = async () => {
    setBusy(true);
    setConnectError(null);
    try {
      const res = await stripeConnectOnboard();
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      const fallback = 'Stripe did not return an onboarding URL. Please try again.';
      setConnectError(fallback);
      toast.error(fallback);
    } catch (e) {
      console.error('Stripe Connect onboard error:', e);
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === 'string' ? raw : (e?.message || 'Could not start Stripe onboarding');
      setConnectError(msg);
      toast.error(msg.length > 120 ? msg.slice(0, 120) + '…' : msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="stripe-connect-panel">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="text-lg font-bold text-white">Payments</h2>
          <p className="text-xs text-zinc-400 mt-1">Connect a bank account to receive payouts from sessions.</p>
        </div>
        {loading ? (
          <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
        ) : status?.charges_enabled && status?.payouts_enabled ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400 font-medium" data-testid="stripe-connected-badge">
            <CheckCircle2 className="w-4 h-4" />
            Connected
          </span>
        ) : status?.connected ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-amber-400 font-medium">
            <AlertCircle className="w-4 h-4" />
            Action needed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm text-zinc-500 font-medium">
            <AlertCircle className="w-4 h-4" />
            Not connected
          </span>
        )}
      </div>

      {!loading && status?.charges_enabled && status?.payouts_enabled ? (
        <div className="space-y-3">
          <div className="text-sm text-zinc-300">
            Your account is set up to receive payments. You&apos;ll get 80% of every session price,
            paid out via Stripe automatically.
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleConnect}
            disabled={busy}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="stripe-update-btn"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <ExternalLink className="w-4 h-4 mr-2" />
            Update Stripe details
          </Button>
        </div>
      ) : !loading ? (
        <div className="space-y-3">
          {status?.connected && !status?.details_submitted && (
            <p className="text-sm text-amber-300">
              You&apos;ve started Stripe onboarding but haven&apos;t finished. Click below to continue.
            </p>
          )}
          {status?.connected && status?.details_submitted && (!status?.charges_enabled || !status?.payouts_enabled) && (
            <p className="text-sm text-amber-300">
              Stripe is reviewing your account. Some details still need verification.
            </p>
          )}
          {!status?.connected && (
            <p className="text-sm text-zinc-300">
              Clients pay through DojoDash via Stripe. We take a 20% platform fee; you keep 80% of every session.
            </p>
          )}
          <Button
            onClick={handleConnect}
            disabled={busy}
            className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
            data-testid="stripe-connect-btn"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {status?.connected ? 'Continue onboarding' : 'Connect bank account'}
          </Button>
          {connectError && (
            <div
              className="mt-3 p-3 rounded-md border border-red-900/60 bg-red-950/40 text-sm text-red-200"
              data-testid="stripe-connect-error"
            >
              <div className="font-medium mb-1">Stripe couldn&apos;t start onboarding</div>
              <div className="text-red-300/90">{connectError}</div>
              {connectError.toLowerCase().includes('connect') && connectError.toLowerCase().includes('sign') && (
                <div className="mt-2 text-xs text-red-300/80">
                  Platform admin: enable Stripe Connect at{' '}
                  <a
                    href="https://dashboard.stripe.com/connect"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-red-200"
                  >
                    dashboard.stripe.com/connect
                  </a>
                  , then retry.
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default StripeConnectPanel;
